"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import React from "react";
import { Link } from "@/components/common/Link";
import { FileText, CreditCard, ClipboardList, CalendarClock, ShieldCheck, RefreshCcw, HardHat, Receipt, Scale, MessageSquare, Lock, Globe, Wrench } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
type Block = {
  type: "p";
  text: string;
} | {
  type: "h";
  label: string;
  text: string;
} | {
  type: "list";
  items: string[];
} | {
  type: "a";
  text: string;
  href: string;
} | {
  type: "table";
  rows: [string, string][];
};
type Section = {
  id: string;
  num: string;
  title: string;
  Icon: typeof FileText;
  blocks: Block[];
};
const SECTIONS: Section[] = [{
  id: "s01",
  num: "01",
  title: "§01 General Terms of Service",
  Icon: FileText,
  blocks: [{
    type: "p",
    text: "By scheduling or accepting any service from Rocket City Water Wizards LLC, you (the customer or authorized agent) acknowledge and agree to the following terms. These terms apply to all residential and commercial pool and spa services, including maintenance, cleaning, diagnostics, repairs, chemical delivery, assessments, upgrades, and specialty treatments, unless otherwise specified in writing through a signed estimate, agreement, or project scope."
  }, {
    type: "p",
    text: "Rocket City Water Wizards is a federally recognized Service-Disabled Veteran-Owned Small Business. Our team is trained and certified through the Pool & Hot Tub Alliance. Services are performed using NSF-certified testing equipment and industry-standard protocols, including chemical balancing to the Langelier Saturation Index guided by Orenda methodologies."
  }, {
    type: "p",
    text: "Services are scheduled based on customer-requested frequency or as defined in a custom plan. Our technicians will arrive on your designated service day and perform the work as specified. We may revise recommended service levels, chemical treatments, or equipment solutions based on evolving conditions, visual inspection, or verified test results. These adjustments are made in the interest of customer safety, water clarity, system health, and equipment longevity."
  }, {
    type: "p",
    text: "All visits are documented with time-stamped photographs, test logs, chemical usage records, and technician notes, available in the customer portal at https://rocketcitywaterwizards.mypoolportal.com. This portal is the official system of record for all job activity. It is the customer's responsibility to review the portal regularly and to keep contact and billing information accurate and current."
  }, {
    type: "p",
    text: "While we work to maintain consistent technician routes, emergencies, weather, holidays, and equipment breakdowns may result in delayed or rescheduled service. We will communicate any such change through your preferred contact method whenever possible."
  }, {
    type: "p",
    text: "These terms may change at any time and will be updated on this page. Continued use of our services constitutes acceptance of the most recent version. Questions about these terms may be directed to (833) 420-9973 or to the department contacts listed in §12."
  }]
}, {
  id: "s02",
  num: "02",
  title: "§02 Payment Terms & Financial Policy",
  Icon: CreditCard,
  blocks: [{
    type: "h",
    label: "Acceptance and electronic signature.",
    text: "By approving a quote or estimate through the customer portal, the customer agrees to this Payment Terms and Financial Policy and agrees to conduct the transaction by electronic means. Approval is made through the customer's portal login and is attributable to the customer. The customer's electronic approval is the customer's electronic signature on the quote and on this Policy, and has the same legal force and effect as a handwritten signature under the Alabama Uniform Electronic Transactions Act and the federal Electronic Signatures in Global and National Commerce Act. All quotes, contracts, statements of work, invoices, service records, photographs, test logs, and this Policy are maintained in and accessible through the customer portal."
  }, {
    type: "h",
    label: "Invoicing and payment.",
    text: "Payment is due in full upon completion of all services, unless a deposit or milestone schedule applies. Rocket City Water Wizards issues a digital invoice and a post-service summary by email within 24 hours of job completion. The summary includes photographs, dosage logs, and service notes, and remains available in the customer portal. If payment is not received within 24 hours of invoicing, the account begins receiving automated reminders by email, and by text message where the customer has consented, until the balance is paid in full. Accepted payment methods are stated on the invoice."
  }, {
    type: "h",
    label: "Recurring service.",
    text: "Weekly and recurring maintenance accounts require an active automatic payment method before the recurring schedule begins. Recurring service is billed in arrears."
  }, {
    type: "h",
    label: "Deposits and milestone draws.",
    text: "A deposit is required at quote approval before work begins. For jobs of $10,000 or less, a 50 percent deposit is due at quote approval and the remaining 50 percent is due upon completion. For jobs over $10,000 and all new pool construction, the deposit amount and milestone draw schedule are defined in that project's Statement of Work or quote. Payments are tied to project milestones, and each draw becomes due upon documented completion of its milestone. Rocket City Water Wizards documents each completed milestone with photographs within 24 hours of completion. The customer has 48 business hours to review the documentation before the corresponding draw is due."
  }, {
    type: "h",
    label: "Milestone confirmation and work progression.",
    text: "Work proceeds in phases according to the milestone draw schedule. The draw for a completed milestone must be paid in full, or confirmed pending, before the work order for the next phase is scheduled or executed. \u201CConfirmed pending\u201D means the customer has initiated payment through an approved method and Rocket City Water Wizards has received confirmation that the payment is in process. If a draw is not paid or confirmed pending, work pauses at the end of the current phase and does not resume until the draw is paid or confirmed. A pause for non-payment is not a breach or default by Rocket City Water Wizards. Any resulting delay is the responsibility of the customer and extends the project schedule accordingly. Rocket City Water Wizards does not order, stage, place, or install equipment or materials for the next phase until its draw is paid or confirmed pending. This structure protects both parties: the customer pays only for milestones that are complete and documented, and Rocket City Water Wizards commits labor, equipment, and materials only after the agreed payment is in place."
  }, {
    type: "h",
    label: "Late payment schedule.",
    text: "A service charge of 1 percent per month, 12 percent per annum, is assessed on the outstanding unpaid balance beginning 15 days after the invoice due date. The charge is simple, not compounding, and accrues on the original unpaid principal only. \u201COutstanding unpaid balance\u201D means the invoice total less any payments, deposits, or credits applied to the account."
  }, {
    type: "list",
    items: ["15 days past due: the monthly service charge begins to accrue, and automated reminders continue.", "30 days past due: routine and recurring service is paused. The Never Green Guarantee is suspended for the period of delinquency.", "45 days past due: the account is referred to collections or legal action. Service charges stop accruing at referral."]
  }, {
    type: "p",
    text: "Rocket City Water Wizards may waive or reduce a service charge at its discretion, including where an account is brought current promptly or where a billing error is confirmed."
  }, {
    type: "h",
    label: "Effect on the Never Green Guarantee.",
    text: "The Never Green Guarantee is conditioned on the account remaining current. The guarantee is suspended for any period during which the account is past due beyond the suspension threshold stated above. Remediation required after a paused service period is quoted and billed separately and is not covered by the guarantee."
  }, {
    type: "h",
    label: "Legal cost recovery.",
    text: "If the account is referred to collections or legal action, the customer agrees to pay the costs of recovery to the fullest extent permitted by applicable law, including reasonable attorneys' fees, court and filing fees, and third-party collection agency fees. This obligation is in addition to the unpaid balance and any service charges accrued before referral."
  }, {
    type: "h",
    label: "Governing law and severability.",
    text: "This Policy is governed by the laws of the State of Alabama. If any provision is held invalid or unenforceable, that provision is modified to the minimum extent necessary to make it enforceable, or severed if it cannot be, and all remaining provisions remain in full force and effect."
  }]
}, {
  id: "s03",
  num: "03",
  title: "§03 Scope of Work & Service Categories",
  Icon: ClipboardList,
  blocks: [{
    type: "p",
    text: "All work performed by Rocket City Water Wizards is based on the scope defined in each job's assigned work order type, which specifies tasks, chemical targets, and technician responsibilities. That work order governs what is authorized, documented, and billable. Services fall into three categories."
  }, {
    type: "h",
    label: "1. Full Service (standard visits).",
    text: "Recurring and one-time jobs including weekly or biweekly cleaning, seasonal openings and closings, algae recovery, filter and salt cell cleaning, water chemistry balancing to LSI standards, skimming, vacuuming, brushing, and basic equipment checks."
  }, {
    type: "p",
    text: "A standard Full Service visit is capped at 2.5 hours onsite, covering arrival, setup, water testing, chemical dosing, debris removal, brushing, vacuuming, and cleanup. Invoicing reflects this allocation as 0.7 man hours, which includes route adjustment, documentation, and system sync. Visits are documented with time-stamped before, during, and after photographs, up to 30 per visit, and logged chemical readings taken with NSF-certified equipment."
  }, {
    type: "p",
    text: "If a technician approaches the 2.5-hour limit with tasks remaining, the technician will notify the customer that a follow-up may be needed, assess route availability for same-day completion, and reschedule remaining tasks for a makeup visit, typically within the same week. Sunday is reserved exclusively as a makeup day for time-sensitive overflow and is not used for routine scheduling. If a Sunday visit becomes necessary, the customer receives at least 24 hours notice."
  }, {
    type: "h",
    label: "2. Repairs and replacements.",
    text: "Diagnostic, replacement, and mechanical work involving pumps, filters, heaters, automation, plumbing, control panels, and other system components. These jobs are scheduled separately, require custom estimates, and are billed based on actual labor time, materials, and scope. They do not fall under Full Service time caps. Repair work does not begin without a defined scope and customer approval."
  }, {
    type: "h",
    label: "3. Specialty services.",
    text: "Non-routine work including water testing visits, stain treatment, enzyme applications, phosphate removal, and green-to-clean recovery. Visit parameters, time frames, and documentation are defined per visit based on severity, equipment condition, and technician findings."
  }, {
    type: "p",
    text: "Rocket City Water Wizards does not perform or invoice for work not listed in the assigned work order. Requests for additional work made onsite require a new work order created through the office. Verbal requests made to a technician in the field are not recorded or executed until the scope is formally updated. All documentation reflects only the work performed as assigned."
  }]
}, {
  id: "s04",
  num: "04",
  title: "§04 Scheduling, Access & Cancellation",
  Icon: CalendarClock,
  blocks: [{
    type: "h",
    label: "Property access.",
    text: "Customers must ensure technicians have full, safe, and unobstructed access to all areas required for service at the scheduled time. This includes unlocked gates and doors, clear paths to the pool, spa, and equipment pad, secured animals, and the absence of safety hazards such as exposed wiring, insect nests, broken concrete, or standing water."
  }, {
    type: "p",
    text: "If a technician cannot safely access the service area on arrival, the visit is documented as an access denial and an Access Denial Fee of $85 is applied to the account. This fee covers route disruption, travel time, the lost service window, and documentation."
  }, {
    type: "p",
    text: "Rocket City Water Wizards technicians will not climb or jump fences, remove or handle animals, enter locked gates, or bypass safety concerns under any circumstance. Customers who repeatedly fail to provide access may be removed from the route schedule at the discretion of management. Access issues must be resolved before the service day. Failure to do so does not exempt the customer from minimum billing or from future prepayment requirements."
  }, {
    type: "h",
    label: "Cancellation and rescheduling.",
    text: "Service must be cancelled or rescheduled at least 48 hours before the scheduled visit. Cancellations or postponements within 48 hours incur a $50 late change fee, payable before further service is provided."
  }, {
    type: "p",
    text: "If a technician arrives and is turned away, or the customer declines service after arrival, the visit is recorded as a same-day cancellation and billed at the full minimum service rate for that visit."
  }, {
    type: "h",
    label: "Arrival windows.",
    text: "Rocket City Water Wizards does not guarantee arrival at an exact time. If a technician is delayed more than 30 minutes beyond the scheduled block, the customer receives a courtesy text update. If the delay exceeds one hour and the day's schedule does not permit same-day service, the job is rescheduled to the next available makeup day. Customers who decline rescheduling may be removed from the regular route and placed on a waitlist."
  }, {
    type: "h",
    label: "Weather.",
    text: "Concrete is not placed when the forecast indicates a 60 percent or greater probability of rain within the working window. Liner installation is not performed when overnight temperatures fall below 60 degrees Fahrenheit without an approved tenting plan, quoted separately. Weather-driven rescheduling is not a breach and extends the project schedule accordingly."
  }]
}, {
  id: "s05",
  num: "05",
  title: "§05 Liability & Limited Warranty",
  Icon: ShieldCheck,
  blocks: [{
    type: "p",
    text: "Rocket City Water Wizards technicians are trained and certified, and hold to manufacturer equipment guidelines and national standards. We use professional-grade tools, NSF-certified testing equipment, and approved industry procedures on every visit. By accepting service, the customer acknowledges the following limitations."
  }, {
    type: "h",
    label: "1. Pre-existing and deferred conditions.",
    text: "Rocket City Water Wizards is not liable for damage or performance issues caused by pre-existing equipment faults, undiagnosed leaks, inadequate or unsafe plumbing, faulty third-party installation, or deferred maintenance. Any defect discovered during a visit is documented with photographs and notes in the customer portal. We do not accept liability for damage that existed before our arrival, including damage not previously visible to the customer."
  }, {
    type: "h",
    label: "2. Weather, environmental, and structural factors.",
    text: "Rocket City Water Wizards is not responsible for damage caused by weather events, environmental runoff, landscape drainage, airborne debris, structural settling, deck movement, tile failure, coping cracks, or animal or pest damage to equipment. Our team documents and notifies the customer of conditions that could contribute to such damage, but is not responsible for remediation absent a separate written agreement."
  }, {
    type: "h",
    label: "3. Customer-modified systems.",
    text: "Systems modified by the customer, by non-certified contractors, or through do-it-yourself installation, including chemical feeders, electrical work, plumbing modifications, and internet-connected automation, are excluded from liability. We do not troubleshoot or service unauthorized equipment and do not warrant any outcome related to modified systems."
  }, {
    type: "h",
    label: "4. Warranty coverage.",
    text: ""
  }, {
    type: "h",
    label: "Service and repair work.",
    text: "Rocket City Water Wizards provides a 30-day limited workmanship warranty on equipment repairs, replacements, and service installations we perform directly, including pump, heater, filter, salt cell, and controller installation, leak patching, and minor plumbing repair. This warranty covers labor defects only."
  }, {
    type: "h",
    label: "New shotcrete pool construction.",
    text: "Pools we build in shotcrete are engineered and PE-stamped by a licensed Professional Engineer before construction, which is what allows us to warrant the structure for ten years. The following coverage applies to shotcrete construction performed by Rocket City Water Wizards:"
  }, {
    type: "table",
    rows: [["Structural shell", "10 years"], ["Plumbing lines, fittings, and installation labor", "2 years"], ["Electrical wiring, bonding, panel work, and lighting", "2 years"], ["Tile and coping installation workmanship", "2 years"], ["Automation system installation", "2 years, stacked on manufacturer warranty"], ["Valves", "Lifetime, parts and labor"], ["Interior finish", "Per material and manufacturer specification, stated in the Statement of Work"], ["Equipment", "Per the schedule stated in the Statement of Work"]]
  }, {
    type: "h",
    label: "Fiberglass and vinyl liner pools.",
    text: "The ten-year structural warranty applies to shotcrete construction only and does not apply to fiberglass or vinyl liner pools. For those installations, the shell or liner is covered by the manufacturer's warranty, and Rocket City Water Wizards provides an installation workmanship warranty as stated in the Statement of Work."
  }, {
    type: "h",
    label: "Site restoration.",
    text: "On new construction, Rocket City Water Wizards restores damage caused by our equipment or crews to pre-construction condition at no cost, including lawn, driveway, landscaping, irrigation, fencing, and lighting along the construction access path, where that restoration is specified in the Statement of Work."
  }, {
    type: "h",
    label: "What warranties do not cover.",
    text: "Parts are covered under manufacturer warranty policies, which vary by brand and model. Customers are responsible for registering and processing manufacturer warranty claims unless stated otherwise in writing. Warranty coverage is void where the system has been modified or serviced by unauthorized parties, where the owner has not maintained equipment per manufacturer guidelines, or where damage results from acts of nature. Warranty work does not include free follow-ups for unrelated service, and does not cover return visits caused by misuse, changed settings, or failure to maintain the system after completion."
  }, {
    type: "h",
    label: "Water chemistry condition.",
    text: "Interior finish warranties are conditioned on the pool being maintained within manufacturer-specified water chemistry parameters. Documented chemistry records are required to support a finish warranty claim."
  }, {
    type: "h",
    label: "Insurance and subcontractors.",
    text: "Rocket City Water Wizards maintains general liability coverage and, where required, workers' compensation. Certificates of insurance are available on request. Portions of construction work, including excavation, concrete placement, electrical, gas, and decking, may be performed by licensed subcontractors under our supervision. Subcontractor work is covered by the same workmanship terms stated above."
  }]
}, {
  id: "s06",
  num: "06",
  title: "§06 Results, Rechecks & Shared Responsibility",
  Icon: RefreshCcw,
  blocks: [{
    type: "p",
    text: "Rocket City Water Wizards performs all services according to national standards, best practices, and the assigned scope of work. Results depend on customer compliance, functioning equipment, and environmental conditions outside our control. If post-service water quality or visual results do not meet expectations, the following applies."
  }, {
    type: "h",
    label: "1. Aftercare instructions not followed.",
    text: "If a customer does not follow the aftercare instructions noted in the job summary, such as running the pump for a specified duration, brushing, adding water, or maintaining flow, and the pool does not respond as expected, Rocket City Water Wizards will conduct one recheck visit at no labor charge to inspect and confirm the cause. A recheck documentation fee not to exceed $4 is applied to log the visit. If instructions were clearly not followed, a full-priced follow-up service is required to restore the pool."
  }, {
    type: "h",
    label: "2. Equipment not functioning properly.",
    text: "If pool equipment is non-functional or partially operational, such as a failed pump, clogged filter, or bad check valve, we document the issue during the recheck and provide a repair quote. Equipment issues are billed at standard service or repair rates based on time and scope. No warranty coverage or free labor applies to equipment failures not caused by our work."
  }, {
    type: "h",
    label: "3. Natural and environmental events.",
    text: "If water clarity or pool condition degrades due to weather, debris, pollen, rainfall, dust, or runoff, Rocket City Water Wizards does not accept responsibility for re-treatment. Follow-up service is billed at full price."
  }, {
    type: "h",
    label: "4. Confirmed service error.",
    text: "If the result was caused by an error on our part, Rocket City Water Wizards will return and correct it. A 0.2 man-hour charge is applied to cover technician deployment and route adjustment, and chemicals used during the corrective visit are billed at standard per-unit rates."
  }, {
    type: "p",
    text: "This structure reflects a shared responsibility between the customer, the technician, the equipment, and the environment."
  }]
}, {
  id: "s07",
  num: "07",
  title: "§07 Construction Terms & Mechanic's Lien Notice",
  Icon: HardHat,
  blocks: [{
    type: "p",
    text: "These terms apply to new pool construction, renovation, and equipment installation projects."
  }, {
    type: "h",
    label: "Alabama lien rights.",
    text: "Rocket City Water Wizards LLC, its subcontractors, and its material suppliers may have lien rights against the improved property under Alabama Code Title 35, Chapter 11, Article 8, if payment for labor or materials furnished is not made. Payment in full to the contractor does not by itself extinguish the rights of unpaid subcontractors or suppliers. This notice is provided for the customer's information as required by Alabama law and is not a statement that any lien has been or will be filed."
  }, {
    type: "h",
    label: "Permits and approvals.",
    text: "Rocket City Water Wizards obtains the building permits and engineering documents required for the scope described in the Statement of Work. The customer is responsible for homeowner association and architectural review approvals, and for disclosing any easement, setback, covenant, or deed restriction affecting the work area. Delays caused by association review or undisclosed restrictions extend the project schedule and are not the responsibility of Rocket City Water Wizards."
  }, {
    type: "h",
    label: "Subsurface conditions.",
    text: "Quotes assume normal excavation conditions. Rock, unstable soil, high groundwater, buried structures, undisclosed utilities, and fill requiring engineered remediation are outside the base scope and are quoted as a change order when encountered."
  }, {
    type: "h",
    label: "Utility locates.",
    text: "Public utility locates are requested before excavation. Private utilities, irrigation lines, invisible fencing, low-voltage lighting, and septic components are the customer's responsibility to locate and mark before work begins. Damage to unmarked private lines is not the responsibility of Rocket City Water Wizards."
  }, {
    type: "h",
    label: "Access and restoration.",
    text: "Heavy equipment access is required for construction. Damage to lawn, driveway, irrigation, or landscaping along the necessary access path is an expected consequence of construction and is not included in restoration unless specified in writing in the Statement of Work."
  }, {
    type: "h",
    label: "Change orders.",
    text: "No change to scope, materials, or price is effective unless issued as a written change order and approved by the customer through the customer portal."
  }, {
    type: "h",
    label: "Substantial completion.",
    text: "The pool is substantially complete when it holds water and the circulation, filtration, and sanitization systems operate as designed. Remaining punch list items do not delay final payment."
  }]
}, {
  id: "s08",
  num: "08",
  title: "§08 Refunds, Credits & Non-Refundable Fees",
  Icon: Receipt,
  blocks: [{
    type: "p",
    text: "Rocket City Water Wizards does not issue cash refunds for services that have been scheduled, started, or completed. Once a technician arrives onsite and begins service, the customer is responsible for payment as outlined in the job summary and these terms."
  }, {
    type: "h",
    label: "No refund on services rendered.",
    text: "No refund is issued for labor or deposit once service has begun. No refund is issued for chemicals applied or prepared during service. Claims based on subjective dissatisfaction with visual results, water clarity, seasonal expectations, or perceived service speed are not grounds for refund where the assigned scope of work was completed."
  }, {
    type: "h",
    label: "Service credits.",
    text: "Rocket City Water Wizards may, at its discretion, issue a service credit for a scheduling error caused by our team, a documented billing mistake reported within 72 hours, or a service interruption caused by force majeure before work began. Credits apply to future services. Credits are not issued as cash, card reversal, electronic transfer, or third-party payment. Credits expire 12 months from the date of issuance unless otherwise specified in writing."
  }, {
    type: "h",
    label: "Non-refundable fees.",
    text: "The following are non-refundable under all circumstances: the Access Denial Fee of $85, the late change fee of $50, the recheck documentation fee, and any route disruption or holiday reschedule fee."
  }, {
    type: "h",
    label: "Chargebacks.",
    text: "Customers who initiate an unauthorized chargeback or payment reversal after service has been rendered will be referred to collections and may be removed from our service schedule."
  }, {
    type: "h",
    label: "Force majeure.",
    text: "Rocket City Water Wizards is not liable for delay or failure to perform caused by events outside its reasonable control, including severe weather, flood, freeze, fire, utility failure, supply chain disruption, labor disruption, government action, or public health emergency. Performance is suspended for the duration of the event and the schedule extends accordingly."
  }]
}, {
  id: "s09",
  num: "09",
  title: "§09 Dispute Resolution & Governing Law",
  Icon: Scale,
  blocks: [{
    type: "h",
    label: "Governing law and venue.",
    text: "These terms are governed by the laws of the State of Alabama. Any legal proceeding arising from or related to service, documentation, billing, equipment, scheduling, or communications must be filed in Madison County, Alabama."
  }, {
    type: "h",
    label: "Informal resolution first.",
    text: "Before initiating formal proceedings, the customer and Rocket City Water Wizards agree to attempt good-faith resolution directly. The customer should submit the concern in writing with supporting detail, and Rocket City Water Wizards will respond within ten business days. Most disputes are resolved at this stage using the photographic and chemical documentation maintained in the customer portal."
  }, {
    type: "h",
    label: "Mediation and arbitration.",
    text: "If informal resolution does not resolve the matter, the parties agree to mediation. If mediation does not resolve the matter, the parties agree to binding arbitration administered by the American Arbitration Association under its Consumer Arbitration Rules, with the arbitrator selected in accordance with those rules. The arbitrator's award is final and enforceable. Arbitration fees are allocated as provided in the AAA Consumer Arbitration Rules."
  }, {
    type: "h",
    label: "Prevailing party costs.",
    text: "In any arbitration or legal proceeding arising under these terms, the prevailing party may be awarded reasonable attorneys' fees and costs."
  }, {
    type: "h",
    label: "Small claims.",
    text: "Either party may bring an individual claim in small claims court in Madison County, Alabama, in lieu of arbitration, where the claim qualifies."
  }, {
    type: "h",
    label: "Jury trial waiver.",
    text: "To the extent permitted by law, both parties waive the right to trial by jury for claims arising under these terms."
  }]
}, {
  id: "s10",
  num: "10",
  title: "§10 SMS & Electronic Communications Consent",
  Icon: MessageSquare,
  blocks: [{
    type: "h",
    label: "Program description.",
    text: "Rocket City Water Wizards LLC operates an account notification and service messaging program for customers who provide a mobile number."
  }, {
    type: "h",
    label: "Message types.",
    text: "Appointment confirmations and reminders, technician arrival notices, weather-related schedule changes, quote and invoice notifications, payment reminders, and account updates."
  }, {
    type: "h",
    label: "Message frequency.",
    text: "Frequency varies by account activity. Weekly service customers typically receive two to six messages per month."
  }, {
    type: "h",
    label: "Cost.",
    text: "Message and data rates may apply. Rocket City Water Wizards does not charge for text messages. Your mobile carrier may."
  }, {
    type: "h",
    label: "Consent is not a condition of purchase.",
    text: "Consent to receive text messages is not required to purchase any good or service. You may decline text messaging and continue to receive all communications by email and telephone."
  }, {
    type: "h",
    label: "How to opt out.",
    text: "Reply STOP to any message to stop all messages. Reply HELP for assistance, or call (833) 420-9973."
  }, {
    type: "h",
    label: "Effect of opting out.",
    text: "Opting out of text messages does not cancel any balance owed, does not end the obligation to pay, and does not relieve the customer of the responsibility to review invoices and account records in the customer portal. Failure to open or respond to any communication does not waive or reduce any financial obligation."
  }, {
    type: "h",
    label: "Carrier liability.",
    text: "Carriers are not liable for delayed or undelivered messages."
  }, {
    type: "h",
    label: "Mobile data privacy.",
    text: "Mobile numbers collected for this program are not sold, rented, or shared with third parties for marketing purposes. No mobile information is shared with third parties or affiliates for marketing or promotional purposes."
  }, {
    type: "h",
    label: "Electronic communications consent.",
    text: "By scheduling or receiving service, the customer agrees to receive account communications electronically, including invoices, service confirmations, payment reminders, and account updates."
  }]
}, {
  id: "s11",
  num: "11",
  title: "§11 Privacy Notice & Data Retention",
  Icon: Lock,
  blocks: [{
    type: "h",
    label: "What we collect.",
    text: "Name, service address, billing address, email address, telephone number, payment method details processed by our payment provider, property and equipment information, service history, water chemistry readings, and photographs of the service area taken in the course of performing work."
  }, {
    type: "h",
    label: "Why we collect it.",
    text: "To schedule and perform service, to document work performed, to invoice and collect payment, to administer warranties, to communicate about the account, and to comply with tax, accounting, and legal obligations."
  }, {
    type: "h",
    label: "Photographs.",
    text: "Photographs taken during service document the condition of the pool, equipment, and work area, and are part of the service record. Photographs are used internally for documentation, warranty administration, and dispute resolution. Photographs are not published, used in marketing, or shared publicly without the property owner's separate written permission."
  }, {
    type: "h",
    label: "What we do not do.",
    text: "We do not sell customer information. We do not rent customer information. We do not share customer information with third parties for their marketing purposes. Mobile numbers are never shared with third parties or affiliates for marketing or promotional purposes."
  }, {
    type: "h",
    label: "Who we share with.",
    text: "Service providers who operate on our behalf and only as needed to perform their function, including our field service software provider, payment processor, messaging provider, and, where an account is referred, a collection agency or attorney. Licensed subcontractors receive only the information needed to perform assigned work."
  }, {
    type: "h",
    label: "Payment data.",
    text: "Rocket City Water Wizards does not store payment card numbers. Card transactions are processed by a PCI-compliant third-party payment processor."
  }, {
    type: "h",
    label: "Data retention.",
    text: "Service records, photographs, water chemistry logs, invoices, and job documentation are retained for a minimum of seven years to support warranty administration, tax and accounting requirements, and dispute resolution."
  }, {
    type: "h",
    label: "Your access.",
    text: "Customers may view their records at any time in the customer portal, and may request a copy of their records or a correction to their account information by written request."
  }, {
    type: "h",
    label: "Children.",
    text: "Our services are directed to property owners and authorized adults. We do not knowingly collect information from children."
  }]
}, {
  id: "s12",
  num: "12",
  title: "§12 Website Terms, Cookies & Accessibility",
  Icon: Globe,
  blocks: [{
    type: "h",
    label: "Website terms of use.",
    text: "The content of this website, including text, photographs, designs, logos, and the Rocket City Water Wizards name and marks, is the property of Rocket City Water Wizards LLC and may not be copied, reproduced, or used without written permission. Content on this site is provided for general information and does not constitute a quote, a warranty, or professional engineering advice for any specific property. Pricing ranges shown are starting points and are not quotes. All quotes are issued through the customer portal."
  }, {
    type: "h",
    label: "Cookies.",
    text: "This site uses cookies and similar technologies for basic site function, to remember preferences, to measure site traffic and performance, and where enabled, for advertising measurement. Essential cookies are required for the site to function. Analytics and advertising cookies can be declined through your browser settings or through the cookie controls on this site where available. Declining non-essential cookies does not affect your ability to request service or contact us."
  }, {
    type: "h",
    label: "Accessibility.",
    text: "Rocket City Water Wizards is working toward conformance with the Web Content Accessibility Guidelines version 2.1, Level AA. If you encounter a barrier to accessing any part of this site, contact us at (833) 420-9973 and we will provide the information you need through an alternate channel and work to correct the issue."
  }, {
    type: "h",
    label: "Contact and routing.",
    text: "Repairs: Solutions@rocketcitywaterwizards.com. Maintenance and weekly service: Services@rocketcitywaterwizards.com. New construction, renovation, liners, and conversions: Sales@rocketcitywaterwizards.com. Scheduling: scheduling@rocketcitywaterwizards.com. Telephone: (833) 420-9973."
  }, {
    type: "h",
    label: "Security disclosure.",
    text: "To report a security vulnerability affecting this site or the customer portal, email info@rocketcitywaterwizards.com with the subject line \u201CVulnerability Report.\u201D"
  }, {
    type: "h",
    label: "Acknowledgment.",
    text: "By scheduling, accepting, or receiving service from Rocket City Water Wizards LLC, the customer confirms they have read, understood, and agreed to all policies on this page. Initiating service through online scheduling, an approved quote, email authorization, or telephone confirmation creates a binding service agreement. Failure to review this page does not release the customer from responsibility or financial obligation."
  }]
}, {
  id: "s13",
  num: "13",
  title: "§13 Manufacturer Warranty Diagnostics & Fees",
  Icon: Wrench,
  blocks: [{
    type: "h",
    label: "In plain English.",
    text: "We diagnose it first. The diagnostic visit is $147.93. If the manufacturer confirms the failure is covered by warranty, we credit that $147.93 back to you. If the manufacturer does not cover it, the $147.93 remains yours to pay. Scheduling a diagnostic does not guarantee the manufacturer will approve the claim. This summary is for convenience; the numbered terms below govern."
  }, {
    type: "h",
    label: "13.1 Scope.",
    text: "This section applies to all manufacturer warranty service requests accepted by Rocket City Water Wizards LLC (“RCWW”), for any manufacturer or product brand, whether or not the equipment or product was originally supplied or installed by RCWW."
  }, {
    type: "h",
    label: "13.2 Relationship to Other Sections.",
    text: "A manufacturer’s warranty is a separate obligation of that manufacturer and is not the same as RCWW’s own limited warranty on workmanship, which is governed by §05. This section governs only diagnostics performed to support a manufacturer’s warranty claim and the fees associated with them. Scheduling and access are governed by §04, payment terms by §02, refunds and credits by §08, and dispute resolution by §09."
  }, {
    type: "h",
    label: "13.3 Diagnostic First.",
    text: "Every manufacturer warranty service request begins with a professional diagnostic evaluation. Warranty eligibility cannot be determined until the equipment or product has been properly diagnosed and the applicable manufacturer’s warranty requirements have been reviewed. RCWW does not submit warranty claims on the basis of a reported symptom alone."
  }, {
    type: "h",
    label: "13.4 Diagnostic Fee.",
    text: "A diagnostic fee of $147.93 is billed for the diagnostic visit. The fee is billed for the visit itself and is earned when the diagnostic is performed, regardless of the outcome of the claim, subject to the credit described in §13.6."
  }, {
    type: "h",
    label: "13.5 What the Fee Covers.",
    text: "Technician dispatch and travel within RCWW’s standard service area; system testing and fault isolation; failure diagnosis; written documentation of findings; photographs of the equipment, failure condition, connections, and site conditions; verification of model number, serial number, and date of manufacture; review of the applicable warranty period and terms; and preparation and submission of, or assistance with, the manufacturer’s warranty claim."
  }, {
    type: "h",
    label: "13.6 If the Failure Is Covered.",
    text: "If the applicable manufacturer confirms the diagnosed failure is covered under its warranty, RCWW credits the full $147.93 diagnostic fee to the customer’s account. The credit is applied automatically against the invoice for the resulting warranty repair. If no further billable work is performed within ninety (90) days of the credit being issued, the customer may request a refund of the credit to the original payment method."
  }, {
    type: "h",
    label: "13.7 Partial Coverage.",
    text: "If the manufacturer approves any portion of the claim (including parts only, labor only, or a partial allowance), the failure is treated as covered for the purpose of §13.6 and the diagnostic fee is credited in full."
  }, {
    type: "h",
    label: "13.8 If the Failure Is Not Covered.",
    text: "If the manufacturer determines the failure is not covered, is excluded, is outside the applicable warranty period, is caused by installation or site conditions, or if no warrantable defect is found, the $147.93 diagnostic fee remains due and payable under the terms of §02. This applies whether or not the customer elects to proceed with any repair."
  }, {
    type: "h",
    label: "13.9 Conditions Commonly Resulting in Denial.",
    text: "Manufacturers commonly deny warranty coverage for conditions including, without limitation: improper installation; improper wiring, grounding, bonding, or electrical supply; plumbing or hydraulic defects; water chemistry damage; scale, corrosion, staining, or chemical damage; freeze damage; insufficient water flow or circulation; improper equipment sizing; external, storm, animal, or impact damage; unauthorized modification, repair, or use of non-approved parts; failure to perform required maintenance; normal wear and consumable components; failure caused by another component or system; products not registered where registration is required; products outside the applicable warranty period; absence of proof of purchase where required; and any other condition excluded by the manufacturer’s published warranty terms. This list is illustrative and is not a statement of any particular manufacturer’s terms."
  }, {
    type: "h",
    label: "13.10 Costs a Manufacturer May Not Cover.",
    text: "A manufacturer’s approval of a warranty claim does not necessarily mean the repair is performed at no cost to the customer. Manufacturers frequently exclude, limit, or pay a fixed allowance toward items including labor, trip and mileage charges, freight and shipping, crane or equipment rental, refrigerant, start-up and programming, water, chemicals, and consumables. Where the manufacturer’s allowance is less than RCWW’s charge for the work performed, the difference is quoted to the customer in advance and requires customer approval before the work proceeds. RCWW will identify any such shortfall in writing before performing billable work."
  }, {
    type: "h",
    label: "13.11 One Fee Per Diagnostic Visit.",
    text: "The diagnostic fee is charged per diagnostic visit. No additional diagnostic fee is charged if RCWW returns to a site because its own initial diagnosis was incomplete or incorrect. An additional diagnostic fee may apply, quoted and approved in advance, where a further visit is required because of an intermittent fault not reproducible at the time of the original visit, a change in the reported condition, additional testing required by the manufacturer, denial of access under §04, or conditions outside RCWW’s control."
  }, {
    type: "h",
    label: "13.12 Non-Covered Work Quoted Separately.",
    text: "Any repair, replacement, labor, materials, parts, shipping, programming, installation, or corrective work not covered by the manufacturer is quoted separately and requires customer approval before additional billable work is performed. RCWW does not perform billable work that has not been approved."
  }, {
    type: "h",
    label: "13.13 Customer Obligations.",
    text: "To support a warranty claim, the customer agrees to provide, on request and to the extent available: proof of purchase, installation date and installer, product registration information, and prior service history; and to provide safe and unobstructed access to the equipment in accordance with §04. Warranty claims may be denied by the manufacturer where required documentation cannot be produced. The diagnostic fee remains due in that circumstance."
  }, {
    type: "h",
    label: "13.14 Manufacturer Timelines.",
    text: "Claim review, authorization, parts availability, return authorizations, freight, and replacement timelines are controlled by the manufacturer and are outside RCWW’s control. RCWW records the claim status on the open job so it is visible to the customer in the client portal. RCWW is not liable for delays, downtime, water loss, chemical costs, or other consequential costs arising from manufacturer processing times, subject to §05."
  }, {
    type: "h",
    label: "13.15 Removal, Shipping, and Depot Repair.",
    text: "Some manufacturers require that equipment be removed and returned to the manufacturer or an authorized depot. Removal, reinstallation, freight, insurance, and temporary equipment are not included in the diagnostic fee and are quoted separately unless the manufacturer expressly covers them. RCWW does not provide loaner or temporary equipment unless separately quoted and approved."
  }, {
    type: "h",
    label: "13.16 No Guarantee of Coverage.",
    text: "Scheduling a warranty diagnostic does not guarantee that the manufacturer will approve the claim. Warranty eligibility, parts coverage, labor allowances, reimbursement, replacement procedures, freight, and all other warranty benefits are determined solely by the applicable manufacturer under its own published warranty terms. RCWW does not determine, modify, extend, waive, or guarantee any manufacturer’s warranty terms, and makes no representation as to the outcome of any claim."
  }, {
    type: "h",
    label: "13.17 Our Role.",
    text: "RCWW diagnoses the reported failure, documents the condition, submits or assists with the applicable warranty claim, and performs authorized warranty work where RCWW is authorized to do so. Where RCWW is not an authorized service provider for a given manufacturer, RCWW will say so and will provide its diagnostic findings and documentation to the customer for use with the manufacturer or an authorized provider. The diagnostic fee remains due in that circumstance."
  }, {
    type: "h",
    label: "13.18 Records.",
    text: "The diagnostic findings, photographs, model and serial verification, and claim reference numbers are recorded on the customer’s job and are available in the client portal for the retention period described in §11."
  }, {
    type: "h",
    label: "13.19 Applicable Manufacturers.",
    text: "This section applies to all manufacturer warranty service accepted by RCWW, including but not limited to the manufacturers and product brands published at rocketcitywaterwizards.com/legal/manufacturers. That list is maintained and updated by RCWW and is incorporated into this section by reference. Inclusion of a manufacturer on that list does not indicate that RCWW is an authorized warranty service provider for that manufacturer, nor that any particular product is within its warranty period."
  }, {
    type: "a",
    text: "View the applicable manufacturers and product brands list",
    href: "/legal/manufacturers"
  }, {
    type: "h",
    label: "13.20 Taxes.",
    text: "Applicable taxes, where required, are shown on the quote and on the invoice."
  }, {
    type: "h",
    label: "13.21 Changes.",
    text: "RCWW may revise this section. The revision identifier published at the top of this page indicates the current version. The terms in effect on the date a diagnostic is approved govern that diagnostic."
  }, {
    type: "h",
    label: "Fee Schedule.",
    text: ""
  }, {
    type: "table",
    rows: [["Manufacturer Warranty Diagnostic (WTY-DIAG). Pool and spa equipment, pumps, filters, heaters and chillers, automation and controls, salt and sanitization systems, robotic and pressure cleaners, lighting, water features, valves, plumbing and hydraulic components, covers and cover systems.", "$147.93"], ["Surface & Structural Warranty Inspection (WTY-INSP). Plaster, aggregate and pebble finishes, tile, coping, vinyl liners, fiberglass and structural shells. Requires chemistry history review and extended documentation.", "Quoted per site"], ["Consumable Product Claims. Chemicals, reagents, test kits, and other consumable products. No diagnostic is performed; claims are referred to the retailer or manufacturer.", "No fee"]]
  }, {
    type: "p",
    text: "Questions about this section can be directed to (833) 420-9973 or solutions@rocketcitywaterwizards.com. Your complete service record, quotes, diagnostic findings, and invoices are available at any time in the client portal."
  }]
}];
function Blocks({
  blocks
}: {
  blocks: Block[];
}) {
  return <>
      {blocks.map((b, i) => {
      if (b.type === "p") {
        return <p key={i} className="text-[hsl(210_2%_85%)]">
              {b.text}
            </p>;
      }
      if (b.type === "h") {
        return <p key={i} className="text-[hsl(210_2%_85%)]">
              <strong className="font-semibold text-white">{b.label}</strong>{" "}
              {b.text}
            </p>;
      }
      if (b.type === "list") {
        return <ul key={i} className="space-y-2 pl-1">
              {b.items.map((item, j) => <li key={j} className="flex gap-3 text-[hsl(210_2%_85%)]">
                  <span className="mt-[0.6em] h-[5px] w-[5px] shrink-0 bg-[hsl(191_91%_47%)]" />
                  <span>{item}</span>
                </li>)}
            </ul>;
      }
      if (b.type === "a") {
        return React.createElement("p", {
          key: i,
          className: "text-[hsl(210_2%_85%)]"
        }, React.createElement(Link, {
          to: b.href,
          className: "text-[hsl(191_91%_47%)] underline underline-offset-4 hover:text-[hsl(210_2%_76%)] transition-colors"
        }, b.text));
      }
      return <div key={i} className="border border-[hsl(0_0%_100%_/_0.1)] divide-y divide-[hsl(0_0%_100%_/_0.08)]">
            {b.rows.map(([label, value], j) => <div key={j} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 px-4 py-3">
                <span className="font-sans text-[15px] text-[hsl(210_2%_85%)] sm:w-1/2 shrink-0">
                  {label}
                </span>
                <span className="font-mono text-[13px] uppercase tracking-wide text-[hsl(191_91%_47%)]">
                  {value}
                </span>
              </div>)}
          </div>;
    })}
    </>;
}
export default function TermsPolicies() {
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: bodyRef,
    isVisible: bodyVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="3311" id="terms-policies" className="bg-[hsl(240_10%_3.9%)] text-[hsl(210_2%_85%)] py-24 md:py-40 px-6 md:px-12 lg:px-20 relative overflow-hidden dark">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#847C8415_1px,transparent_1px),linear-gradient(to_bottom,#847C8415_1px,transparent_1px)] bg-[size:50px_50px] pointer-events-none" />

      <div className="max-w-[1400px] mx-auto relative z-10">
        <div className="flex items-center justify-between mb-16 md:mb-24 border-b border-[hsl(0_0%_100%_/_0.1)] pb-6">
          <div className="font-mono text-xs md:text-sm uppercase tracking-widest text-[hsl(191_91%_47%)] flex items-center gap-2" style={{
          letterSpacing: "0.2em"
        }}>
            <span className="w-2 h-2 bg-[hsl(191_91%_47%)] inline-block" />
            06 / LEGAL &amp; COMPLIANCE
          </div>
          <div className="font-mono text-xs text-[hsl(300_3%_50%)] hidden md:block">
            HUNTSVILLE, AL // REV 2026.1
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          {}
          <div ref={titleRef} className={`lg:col-span-4 transition-all duration-1000 ease-out ${titleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="lg:sticky lg:top-32 space-y-8">
              <div>
                <span className="font-mono text-xs uppercase tracking-wider text-[hsl(191_91%_47%)] block mb-3">
                  [SPEC: PROTOCOL-01]
                </span>
                <h1 className="font-sans font-bold text-4xl md:text-5xl lg:text-[56px] leading-[1.1] text-[hsl(210_2%_85%)]" style={{
                letterSpacing: "-0.04em"
              }}>
                  Terms &amp;
                  <br />
                  Policies
                </h1>
                <p className="font-sans text-sm md:text-base text-[hsl(300_3%_50%)] mt-4">
                  Rocket City Water Wizards LLC &middot; Last updated August 2026
                </p>
              </div>

              <nav className="border border-[hsl(0_0%_100%_/_0.1)] bg-[hsl(240_10%_6%)] p-5">
                <div className="font-mono text-xs uppercase tracking-wider text-[hsl(191_91%_47%)] mb-4">
                  &gt;_ SECTION INDEX
                </div>
                <ul className="space-y-2">
                  {SECTIONS.map(s => <li key={s.id}>
                      <Link to={`#${s.id}`} className="font-mono text-[12px] text-[hsl(300_3%_60%)] hover:text-[hsl(191_91%_47%)] transition-colors block leading-snug">
                        {s.num}. {s.title.replace(/^§\d+\s/, "")}
                      </Link>
                    </li>)}
                </ul>
              </nav>

              <div className="font-mono text-[11px] leading-relaxed text-[hsl(300_3%_50%)] space-y-1">
                <div>AL LICENSE 2608580</div>
                <div>EIN 83-1114912</div>
                <div>UEI IDXXV8ZJMBADF9</div>
                <div>CAGE 18E31 &middot; DUNS 117231165</div>
                <div>EST. 28 JUNE 2018 &middot; SDVOSB</div>
              </div>
            </div>
          </div>

          {}
          <div ref={bodyRef} className={`lg:col-span-7 lg:col-start-6 space-y-6 transition-all duration-1000 ease-out ${bodyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {SECTIONS.map(({
            id,
            num,
            title,
            Icon,
            blocks
          }) => <div key={id} id={id} className="border border-[hsl(0_0%_100%_/_0.1)] bg-[hsl(240_10%_6%)] relative rounded-lg overflow-hidden scroll-mt-32">
                <div className="absolute top-4 right-4 z-10 px-3 py-1 bg-[hsl(191_91%_47%_/_0.1)] border border-[hsl(191_91%_47%)] font-mono text-[10px] text-[hsl(191_91%_47%)]">
                  SECTION {num}
                </div>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value={id} className="border-b-0">
                    <AccordionTrigger className="px-6 md:px-8 py-6 hover:no-underline">
                      <div className="flex items-center gap-3 text-left">
                        <Icon className="size-5 text-[hsl(191_91%_47%)] shrink-0" />
                        <h2 className="font-sans font-bold text-lg md:text-xl text-[hsl(210_2%_85%)] pr-20">
                          {title}
                        </h2>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 md:px-8 pb-8 pt-0 font-sans text-[16px] md:text-[17px] space-y-4 max-w-[70ch]" style={{
                  lineHeight: "1.65"
                }}>
                      <Blocks blocks={blocks} />
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>)}

            <div className="mt-12 p-6 md:p-8 border border-[hsl(191_91%_47%_/_0.3)] bg-[hsl(240_10%_6%)] rounded-lg">
              <p className="font-sans text-[16px] md:text-[17px] text-[hsl(210_2%_85%)] max-w-[70ch]" style={{
              lineHeight: "1.65"
            }}>
                Questions about any policy on this page can be directed to{" "}
                <span className="text-[hsl(191_91%_47%)]">(833) 420-9973</span>{" "}
                or to the department contacts listed in §12. Your complete
                service record, quotes, and invoices are available at any time in
                the customer portal.
              </p>
              <div className="mt-6 pt-6 border-t border-[hsl(0_0%_100%_/_0.1)] flex items-center justify-between flex-wrap gap-4">
                <span className="font-mono text-xs text-[hsl(300_3%_50%)]">
                  THEIR BEST IS OUR BASELINE
                </span>
                <Link to="#" className="inline-flex items-center justify-center px-6 py-3 bg-[hsl(191_91%_47%)] text-[hsl(240_10%_3.9%)] font-sans font-semibold rounded-lg hover:bg-[hsl(210_2%_76%)] transition-colors text-sm">
                  Client Portal
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}